
import { Component } from "vue-property-decorator";
import { Getter, Mutation } from "vuex-class";
import { Namespace } from "@/store/types";
import { MyChannel } from "@/services/apiService/api.types";
import Swiper, { SwiperOptions } from "swiper";
import SpatialNavigation from "spatial-navigation-js";
import { keyCodeFinder } from "@/services/keyCodeService/keyCodeService";
import { KeySet } from "@/services/keyCodeService/keyCode.types";
import {
  ChannelsPageGetter,
  ChannelsPageMutation,
  ViewType
} from "@/components/channelsPage/types";
import { ChannelCategoriesFilter } from "@/mixins/ChannelCategoriesFilter";
import { mixins } from "vue-class-component";
import { Categories } from "@/helpers/constants";
import ChannelCard from "@/components/channelsPage/components/ChannelCard.vue";
import {
  debounce,
  getBreakpoint,
  MediaQuery,
  scrollToPage
} from "@/helpers/utils";
import apiStorageInterface from "@/services/apiStorageInterface";
import EpgCard from "@/components/channelsPage/components/EpgCard.vue";
import DateTimeSection from "@/components/DateTimeSection.vue";
import InfoModal from "@/components/channelsPage/components/InfoModal.vue";
import EpgModal from "@/components/channelsPage/components/EpgModal.vue";
import { LocaleChannelsPage } from "@/plugins/i18n/types";
import { getTranslatedCategory } from "@/helpers/utils";
import { EventBus } from "@/main";

@Component({
  components: {
    DateTimeSection,
    EpgCard,
    ChannelCard,
    InfoModal,
    EpgModal
  }
})
export default class ChannelsPage extends mixins(ChannelCategoriesFilter) {
  @Mutation(ChannelsPageMutation.setCardControls, {
    namespace: Namespace.ChannelsPage
  })
  setControlsActive!: (cardControls: boolean) => void;

  @Getter(ChannelsPageGetter.GetCardControls, {
    namespace: Namespace.ChannelsPage
  })
  getCardControlsStatus!: boolean;

  @Getter(ChannelsPageGetter.GetPlayedChannelCategory, {
    namespace: Namespace.ChannelsPage
  })
  getPlayedChannelCategory?: Categories;

  @Getter(ChannelsPageGetter.GetPlayedChannelIndex, {
    namespace: Namespace.ChannelsPage
  })
  getPlayedChannelIndex?: number;

  @Getter(ChannelsPageGetter.GetPlayedChannelView, {
    namespace: Namespace.ChannelsPage
  })
  getPlayedChannelView?: ViewType;

  @Getter(ChannelsPageGetter.getShowEpgScreen, {
    namespace: Namespace.ChannelsPage
  })
  getShowEpgScreen!: boolean;

  @Getter(ChannelsPageGetter.getShowInfoScreen, {
    namespace: Namespace.ChannelsPage
  })
  getShowInfoScreen!: boolean;

  activeSlideIndex = 0;
  slidesPerRow = 0;
  slidesToRender = 15;

  viewType = ViewType;
  shownViewType: ViewType = ViewType.ChannelEpg;

  categoriesType = Categories;

  swiperOptions: SwiperOptions = {
    slidesPerView: 5,
    spaceBetween: 40
  };

  categorySwiper: Swiper | undefined;
  getTranslatedCategory = getTranslatedCategory;

  handleResizeDebounce: () => void = debounce(this.setSlidesPerRow, 50);
  handleSpatialNavDebounce: () => void = debounce(SpatialNavigation.resume, 50);

  scrollFuncRef = scrollToPage;

  LocaleChannelsPage = LocaleChannelsPage;

  async mounted(): Promise<void> {
    await this.setCategories();
    await this.filteredCategoriesFunc();

    this.$nextTick(() => {
      this.categorySwiper = new Swiper(".swiper", this.swiperOptions);

      //needed for registering clicks on swiper elements
      this.categorySwiper.on("click", this.focusCategory);

      //making sure no accidental sliding happens
      this.categorySwiper.keyboard?.disable();
    });

    window.addEventListener("resize", this.handleResizeDebounce);
    if (document.getElementById("channelsContainer")) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      //@ts-ignore
      document
        .getElementById("channelsContainer")
        .addEventListener("scroll", this.scrollLazyLoad);
    }
    this.setSlidesPerRow(); //setting initial value
    this.focusCategory(); //focusing category of played channel if it is set

    if (this.getPlayedChannelView) {
      this.shownViewType = this.getPlayedChannelView;
    }
    // calculating minimum amount of cards to render or the played channels card to appear
    if (this.getPlayedChannelIndex) {
      const remainder = this.getPlayedChannelIndex % 15;
      let wholeNum = Math.floor(this.getPlayedChannelIndex / 15);

      if (wholeNum !== 0) {
        this.slidesToRender = wholeNum * 15;
      }
      if (wholeNum !== 0 && remainder) {
        this.slidesToRender = (wholeNum + 1) * 15;
      }
    }

    //focusing channel after closing overlay
    EventBus.$on("cantRefocus", () => {
      const firstAvailable = document.getElementsByClassName(
        "card epg_card focusable"
      )[0].id;
      SpatialNavigation.focus("#" + firstAvailable);
      this.setControlsActive(false);
    });
    SpatialNavigation.resume();
  }

  get activeCategoryChannels(): MyChannel[] | undefined {
    if (this.categories[this.filteredCategories[this.activeSlideIndex]]) {
      return this.categories[this.filteredCategories[this.activeSlideIndex]]
        .channels;
    }

    return undefined;
  }

  get activeCategoryShownChannels(): MyChannel[] | undefined {
    if (this.activeCategoryChannels) {
      if (this.activeCategoryChannels.length > this.slidesToRender) {
        return this.activeCategoryChannels.slice(0, this.slidesToRender);
      }
      if (this.activeCategoryChannels.length <= this.slidesToRender) {
        return this.activeCategoryChannels;
      }
    }

    return undefined;
  }

  async updateChannelList(e: Event): Promise<void> {
    await apiStorageInterface.fetchSortedCategories(true);
    await this.setCategories();
    await this.filteredCategoriesFunc();
    this.setControlsActive(false);
    SpatialNavigation.focus("#channel" + e);
  }

  setSlidesPerRow(): void {
    switch (getBreakpoint()) {
      case MediaQuery.sm:
      case MediaQuery.md:
      case MediaQuery.lg:
        this.slidesPerRow = 3;
        break;
      case MediaQuery.xl:
        this.slidesPerRow = 4;
        break;
      case MediaQuery.xxl:
        this.slidesPerRow = 5;
        break;
    }
  }

  handleFilterActions(event: KeyboardEvent): void {
    SpatialNavigation.pause();
    switch (keyCodeFinder(event.keyCode)) {
      case KeySet.KEY_LEFT:
        if (this.activeSlideIndex === 0) {
          SpatialNavigation.resume();
          SpatialNavigation.focus("#" + this.$route.name);
          return;
        }

        this.slideLeft();
        break;
      case KeySet.KEY_RIGHT:
        this.slideRight();
        break;
      case KeySet.KEY_UP:
        SpatialNavigation.focus("#" + this.shownViewType);
        SpatialNavigation.resume();
        break;
      case KeySet.KEY_DOWN:
        if (this.activeCategoryChannels) {
          SpatialNavigation.focus(
            "#channel" + this.activeCategoryChannels[0].id
          );
          this.handleSpatialNavDebounce();
        }
        break;
    }
  }

  slideRight(): void {
    if (
      this.activeSlideIndex >= 2 &&
      this.activeSlideIndex <= this.filteredCategories.length - 3
    ) {
      this.categorySwiper?.slideNext();
    }

    if (this.activeSlideIndex < this.filteredCategories.length - 1)
      this.activeSlideIndex++;
    this.slidesToRender = 15;
    SpatialNavigation.focus("#id" + this.activeSlideIndex);
  }

  slideLeft(): void {
    if (
      this.activeSlideIndex >= 2 &&
      this.activeSlideIndex <= this.filteredCategories.length
    ) {
      this.categorySwiper?.slidePrev();
    }

    if (this.activeSlideIndex > 0) this.activeSlideIndex--;
    this.slidesToRender = 15;
    SpatialNavigation.focus("#id" + this.activeSlideIndex);
  }

  handleChannelRefocus(event: KeyboardEvent, channelIndex: number): void {
    if (!this.getCardControlsStatus) {
      SpatialNavigation.pause();
      switch (keyCodeFinder(event.keyCode)) {
        case KeySet.KEY_LEFT:
          SpatialNavigation.resume();
          if (channelIndex % this.slidesPerRow === 0) {
            SpatialNavigation.focus("#" + this.$route.name);
            return;
          }
          SpatialNavigation.focus();
          break;
        case KeySet.KEY_UP:
          if (channelIndex < this.slidesPerRow) {
            SpatialNavigation.focus("#id" + this.activeSlideIndex);
            this.handleSpatialNavDebounce();
            return;
          }
          SpatialNavigation.resume();
          SpatialNavigation.focus();
          break;
        default:
          SpatialNavigation.resume();
          SpatialNavigation.focus();
          break;
      }
    }
  }

  lazyLoadCards(channelIndex: number): void {
    if (
      this.activeCategoryShownChannels &&
      this.activeCategoryChannels &&
      channelIndex >= this.activeCategoryShownChannels.length / 2
    ) {
      this.slidesToRender + 15 < this.activeCategoryChannels.length
        ? (this.slidesToRender += 15)
        : (this.slidesToRender += this.activeCategoryChannels.length - 1);
    }
  }

  //for focusing a specific category on click or when refocusing played channels
  focusCategory(): void {
    const oldActiveSlideIndex = this.activeSlideIndex;
    //for compatibility with the click handler and channel refocus logic
    let desiredSlideIndex = this.getPlayedChannelCategory
      ? this.filteredCategories.indexOf(this.getPlayedChannelCategory)
      : this.categorySwiper?.clickedIndex
      ? this.categorySwiper.clickedIndex
      : 0;

    if (oldActiveSlideIndex < desiredSlideIndex) {
      for (let i = 0; i < desiredSlideIndex - oldActiveSlideIndex; i++) {
        this.slideRight();
      }
    }
    if (oldActiveSlideIndex > desiredSlideIndex) {
      for (let i = 0; i < oldActiveSlideIndex - desiredSlideIndex; i++) {
        this.slideLeft();
      }
    }
  }

  scrollLazyLoad(): void {
    if (this.activeCategoryShownChannels && this.activeCategoryChannels) {
      const triggerIndex = Math.floor(
        this.activeCategoryShownChannels?.length / 2
      );
      const triggerId =
        "channel" + this.activeCategoryShownChannels[triggerIndex].id;
      const element = document.getElementById(triggerId);
      if (element) {
        const rect = element.getBoundingClientRect();
        //checking if the channel card selected is on screen
        if (
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <=
            (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <=
            (window.innerWidth || document.documentElement.clientWidth)
        ) {
          this.slidesToRender + 15 < this.activeCategoryChannels.length
            ? (this.slidesToRender += 15)
            : (this.slidesToRender += this.activeCategoryChannels.length - 1);
        }
      }
    }
  }

  beforeDestroy(): void {
    window.removeEventListener("resize", this.handleResizeDebounce);
  }
}
